import { useMemo } from 'react'
import { useTranslation } from 'next-i18next'
import { useFormContext } from 'react-hook-form'
import { useRouter } from 'next/router'
import { Text, useDisclosure } from '@chakra-ui/react'
import { CustomizableEngraveOption } from '_app/graphql/generated/graphql'
import { APP_ROUTES } from '_app/routes'
import { EngravingEntity } from '_app/types/cms'
import { getSizeById } from '../utils/size'
import { ConfiguratorVideoModal } from './ConfiguratorVideoModal'
import dynamic from 'next/dynamic'
import {GTM_EVENTS} from '../../../_app/utils/dataLayer'

const DynamicCartEngravingSkeleton = dynamic(() => import('modules/cart/components/CartEngravingSkeleton'))

interface ProductEngraveProps {
  product: any;
  requiredOptions: any;
  engravingsData: EngravingEntity[];
}
const PAINTING_CODES = ['malowanie_1', 'malowanie_2']
export const ENGRAVING_CODES = [
  'graw_reczny',
  'graw_masz_spec',
  'graw_masz_spec_1',
  'graw_masz_spec_2',
]
const ProductEngrave = ({
  product,
  requiredOptions,
  engravingsData,
}: ProductEngraveProps) => {
  const { t } = useTranslation(['engraving', 'common', 'product'])
  const router = useRouter()
  const { watch } = useFormContext()
  const { isOpen, onOpen, onClose } = useDisclosure()
  const selectedOptions = watch('selected_options')?.flat()
  const paintingOptions = (product?.options as any)?.filter(option =>
      option?.__typename === 'CustomizableEngraveOption' && PAINTING_CODES.includes(option?.value?.code || '')
  )
  const engravingOptions = (
    product?.options as CustomizableEngraveOption[]
  )?.filter(
    option =>
      option?.__typename === 'CustomizableEngraveOption' &&
      ENGRAVING_CODES.includes(option?.value?.code || '')
  )
  const productPrice = product?.price_range?.minimum_price?.final_price
  const getProductItems = () => {
    if (!product) {
      return []
    }
    if (!product.items) {
      return [{ options: [{ product }] }]
    }
    return product.items
  }
  const productItems = getProductItems()
  const productConfiguration = useMemo(() => {
    try {
      return product.setup_config_new
        ? JSON.parse(product.setup_config_new)
        : undefined
    } catch (e) {
      return undefined
    }
  }, [product])
  const showCustomizationButton = !!productConfiguration || !!engravingOptions?.length
  const handleProductConfigurator = () => {
    GTM_EVENTS.configureProduct()
    const size = getSizeById(selectedOptions, requiredOptions)
    const query = {
      ...productConfiguration,
      size,
      slots: JSON.stringify(productConfiguration.slots),
      url_key: product.url_key,
    }
    router.push({
      pathname: APP_ROUTES.CONFIGURATOR,
      query,
    })
  }
  // we're not displaying customization tutorial video on german website
  const showCustomizationTutorial = productConfiguration && router.locale === 'pl'
  return (
    <>
      {paintingOptions?.length > 0 && (
        <DynamicCartEngravingSkeleton
          btn={{
            img: '/images/product/drawer/engr_dk.png',
            text: t('ADD_PAINTING'),
          }}
          options={paintingOptions}
          productPrice={productPrice}
          productName={product.name}
          productItems={productItems}
        />
      )}
      {showCustomizationButton && (
        <>
          {showCustomizationTutorial && (
            <>
              <Text
                suppressHydrationWarning={true}
                cursor="pointer"
                textDecoration="underline"
                fontSize="xs"
                mb={2}
                textAlign="right"
                onClick={onOpen}
              >
                {t('HOW_TO_CONFIGURE')}
              </Text>
               <ConfiguratorVideoModal isOpen={isOpen} onClose={onClose} />
            </>
          )}
          <DynamicCartEngravingSkeleton
            btn={{
              img: '/images/product/drawer/engr_manual_btn.webp',
              text: t('PERSONALIZE_PRODUCT'),
              textDetails: productConfiguration
                ? t('CHOOSE_MATERIAL_COLOR')
                : t('ADD_ENGRAVING'),
              onClick: productConfiguration
                ? handleProductConfigurator
                : undefined,
            }}
            options={engravingOptions}
            productPrice={productPrice}
            productName={product.name}
            productItems={productItems}
            engravingsData={engravingsData}
          />
        </>
      )}
    </>
  )
}

export default ProductEngrave
